import axios from "axios"
import {
    BatchTrackingRequest,
    BatchTrackingResponse
} from "../interface/TrackingInterface"

const TRK: any = process.env.REACT_APP_BASE_URL_TRACK
const BATCH_TRK: any = process.env.REACT_APP_BASE_URL_BATCH
const BATCH_ALL_OCCURRENCES_TRK: any =
    process.env.REACT_APP_BASE_URL_BATCH_ALL_OCCURRENCES
const REACT_APP_BASE_URL_BATCH_ALL_OCCURRENCES_UNIT: any =
    process.env.REACT_APP_BASE_URL_BATCH_ALL_OCCURRENCES_UNIT

export const TRACKING_ERROR_CODES = {
    CONNECTION_REFUSED: "TRK001",
    CONNECTION_REFUSED_EXECUTING_POST: "TRK008",
    NETWORK_ERROR: "TRK002",
    TIMEOUT_ERROR: "TRK003",
    API_ERROR: "TRK004",
    INVALID_RESPONSE: "TRK005",
    NO_INVOICE_FOUND: "TRK009",
    NOT_FOUND: "TRK006",
    INVALID_DATA: "TRK007",
    UNKNOWN_ERROR: "TRK999"
}

export const ERROR_MESSAGE_MAPPING = {
    "Connection refused executing POST":
        TRACKING_ERROR_CODES.CONNECTION_REFUSED_EXECUTING_POST,
    "Connection refused": TRACKING_ERROR_CODES.CONNECTION_REFUSED,
    "Network Error": TRACKING_ERROR_CODES.NETWORK_ERROR,
    timeout: TRACKING_ERROR_CODES.TIMEOUT_ERROR,
    ECONNREFUSED: TRACKING_ERROR_CODES.CONNECTION_REFUSED,
    ENOTFOUND: TRACKING_ERROR_CODES.NETWORK_ERROR
}

export interface TrackingErrorResponse {
    code: string
    message: string
    originalError?: any
}

const TrackingService = {
    getInformation: async (
        cpfOrCnpj: string,
        invoiceNumber: string
    ): Promise<any> => {
        const body = {
            notafiscal: {
                cpfCnpjDestinatario: cpfOrCnpj,
                numero: invoiceNumber
            }
        }

        try {
            const response = await axios.post(TRK, body)

            if (response.data && response.data.status === "erro") {
                const errorMessage = response.data.mensagem || "Erro da API"

                if (
                    errorMessage
                        .toUpperCase()
                        .includes("NENHUMA NOTA FISCAL ENCONTRADA")
                ) {
                    const customError: TrackingErrorResponse = {
                        code: TRACKING_ERROR_CODES.NO_INVOICE_FOUND,
                        message:
                            "Nenhuma nota fiscal encontrada para os parâmetros fornecidos",
                        originalError: response.data
                    }
                    throw customError
                }

                if (errorMessage.includes("33 -")) {
                    const customError: TrackingErrorResponse = {
                        code: TRACKING_ERROR_CODES.NO_INVOICE_FOUND,
                        message: errorMessage,
                        originalError: response.data
                    }
                    throw customError
                }

                const customError: TrackingErrorResponse = {
                    code: TRACKING_ERROR_CODES.API_ERROR,
                    message: errorMessage,
                    originalError: response.data
                }
                throw customError
            }

            return response
        } catch (error: any) {
            if (error.code && error.message) {
                throw error
            }

            let errorCode = TRACKING_ERROR_CODES.UNKNOWN_ERROR
            let errorMessage = "Erro desconhecido"

            if (error.response) {
                errorCode = TRACKING_ERROR_CODES.API_ERROR
                errorMessage = `Erro da API: ${error.response.status} - ${error.response.statusText}`
            } else if (error.request) {
                const errorString = error.message || error.toString()

                for (const [messagePattern, code] of Object.entries(
                    ERROR_MESSAGE_MAPPING
                )) {
                    if (
                        errorString
                            .toLowerCase()
                            .includes(messagePattern.toLowerCase())
                    ) {
                        errorCode = code
                        break
                    }
                }

                switch (errorCode) {
                    case TRACKING_ERROR_CODES.CONNECTION_REFUSED_EXECUTING_POST:
                        errorMessage =
                            "Conexão recusada ao executar requisição POST"
                        break
                    case TRACKING_ERROR_CODES.CONNECTION_REFUSED:
                        errorMessage = "Conexão recusada pelo servidor"
                        break
                    case TRACKING_ERROR_CODES.NETWORK_ERROR:
                        errorMessage = "Erro de conexão de rede"
                        break
                    case TRACKING_ERROR_CODES.TIMEOUT_ERROR:
                        errorMessage = "Tempo limite de conexão excedido"
                        break
                    default:
                        errorMessage = "Erro de conexão com o servidor"
                }
            } else {
                errorCode = TRACKING_ERROR_CODES.INVALID_DATA
                errorMessage = "Erro na configuração da requisição"
            }

            const customError: TrackingErrorResponse = {
                code: errorCode,
                message: errorMessage,
                originalError: error
            }

            throw customError
        }
    },

    getBatchInformation: async (
        trackingItems: Array<{ numero: string; serie: string }>,
        authToken: string
    ): Promise<BatchTrackingResponse[]> => {
        if (!BATCH_TRK) {
            throw new Error("Endpoint de rastreamento em lote não configurado")
        }

        if (!authToken) {
            throw new Error(
                "Token de autenticação é obrigatório para rastreamento em lote"
            )
        }
        const body: BatchTrackingRequest = {
            rastreamentos: trackingItems.map((item) => ({
                notafiscal: {
                    numero: item.numero,
                    serie: item.serie
                }
            }))
        }

        try {
            const response = await axios.post(BATCH_TRK, body, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authToken
                },
                timeout: 30000
            })
            if (response.data && Array.isArray(response.data)) {
                return response.data.map((item: any) => ({
                    notafiscal: {
                        cnpjFornecedor:
                            item.cnpjFornecedor ||
                            item.notafiscal?.cnpjFornecedor,
                        pedido: item.pedido || item.notafiscal?.pedido,
                        numero: item.numero || item.notafiscal?.numero || "",
                        serie: item.serie || item.notafiscal?.serie || "",
                        cpfCnpjDestinatario:
                            item.cpfCnpjDestinatario ||
                            item.notafiscal?.cpfCnpjDestinatario,
                        destinatario:
                            item.destinatario ||
                            item.notafiscal?.destinatario ||
                            "",
                        cnpjTransportadora:
                            item.cnpjTransportadora ||
                            item.notafiscal?.cnpjTransportadora ||
                            "",
                        emissao: item.emissao || item.notafiscal?.emissao,
                        dataExpedicao:
                            item.dataExpedicao ||
                            item.notafiscal?.dataExpedicao,
                        remetente: item.remetente || item.notafiscal?.remetente,
                        datalimite:
                            item.datalimite || item.notafiscal?.datalimite,
                        ufRementente:
                            item.ufRementente || item.notafiscal?.ufRementente,
                        endRem: item.endRem || item.notafiscal?.endRem,
                        munRem: item.munRem || item.notafiscal?.munRem,
                        bairroRem: item.bairroRem || item.notafiscal?.bairroRem,
                        cepRem: item.cepRem || item.notafiscal?.cepRem,
                        Status: item.status || item.Status || "pendente",
                        endereco: {
                            logradouro: item.endereco?.logradouro,
                            bairro: item.endereco?.bairro,
                            municipio:
                                item.endereco?.municipio ||
                                item.municipio ||
                                "",
                            uf: item.endereco?.uf || item.uf || "",
                            cep: item.endereco?.cep
                        },
                        linkComprovante: item.linkComprovante || ""
                    },
                    ocorrencias: (item.ocorrencias || []).map(
                        (ocorrencia: any) => ({
                            numOcorrencia: ocorrencia.numOcorrencia,
                            codigo: ocorrencia.codigo || "",
                            descricao: ocorrencia.descricao || "",
                            codigoKronos: ocorrencia.codigoKronos,
                            descricaoKronos: ocorrencia.descricaoKronos,
                            status: ocorrencia.status || "",
                            momento: ocorrencia.momento || ""
                        })
                    )
                }))
            } else if (response.data && response.data.rastreamentos) {
                // Caso a resposta venha em formato diferente
                return response.data.rastreamentos.map((item: any) => ({
                    notafiscal: {
                        cnpjFornecedor:
                            item.cnpjFornecedor ||
                            item.notafiscal?.cnpjFornecedor,
                        pedido: item.pedido || item.notafiscal?.pedido,
                        numero: item.numero || item.notafiscal?.numero || "",
                        serie: item.serie || item.notafiscal?.serie || "",
                        cpfCnpjDestinatario:
                            item.cpfCnpjDestinatario ||
                            item.notafiscal?.cpfCnpjDestinatario,
                        destinatario:
                            item.destinatario ||
                            item.notafiscal?.destinatario ||
                            "",
                        cnpjTransportadora:
                            item.cnpjTransportadora ||
                            item.notafiscal?.cnpjTransportadora ||
                            "",
                        emissao: item.emissao || item.notafiscal?.emissao,
                        dataExpedicao:
                            item.dataExpedicao ||
                            item.notafiscal?.dataExpedicao,
                        remetente: item.remetente || item.notafiscal?.remetente,
                        datalimite:
                            item.datalimite || item.notafiscal?.datalimite,
                        ufRementente:
                            item.ufRementente || item.notafiscal?.ufRementente,
                        endRem: item.endRem || item.notafiscal?.endRem,
                        munRem: item.munRem || item.notafiscal?.munRem,
                        bairroRem: item.bairroRem || item.notafiscal?.bairroRem,
                        cepRem: item.cepRem || item.notafiscal?.cepRem,
                        Status: item.status || item.Status || "pendente",
                        endereco: {
                            logradouro: item.endereco?.logradouro,
                            bairro: item.endereco?.bairro,
                            municipio:
                                item.endereco?.municipio ||
                                item.municipio ||
                                "",
                            uf: item.endereco?.uf || item.uf || "",
                            cep: item.endereco?.cep
                        },
                        linkComprovante: item.linkComprovante || ""
                    },
                    ocorrencias: (item.ocorrencias || []).map(
                        (ocorrencia: any) => ({
                            numOcorrencia: ocorrencia.numOcorrencia,
                            codigo: ocorrencia.codigo || "",
                            descricao: ocorrencia.descricao || "",
                            codigoKronos: ocorrencia.codigoKronos,
                            descricaoKronos: ocorrencia.descricaoKronos,
                            status: ocorrencia.status || "",
                            momento: ocorrencia.momento || ""
                        })
                    )
                }))
            } else {
                throw new Error("Formato de resposta inválido do servidor")
            }
        } catch (error: any) {
            if (error.response) {
                const errorMessage =
                    error.response.data?.message ||
                    error.response.data?.mensagem ||
                    `Erro da API: ${error.response.status}`
                throw new Error(errorMessage)
            } else if (error.request) {
                if (error.code === "ECONNABORTED") {
                    throw new Error("Tempo limite de conexão excedido")
                }
                throw new Error("Erro de conexão com o servidor")
            } else {
                throw new Error(error.message || "Erro desconhecido")
            }
        }
    },

    // Método para rastreamento em lote com todas as ocorrências
    getBatchAllOccurrences: async (
        trackingItems: Array<{ numero: string; serie: string }>,
        authToken: string
    ): Promise<BatchTrackingResponse[]> => {
        if (!BATCH_ALL_OCCURRENCES_TRK) {
            throw new Error(
                "Endpoint de rastreamento em lote com todas as ocorrências não configurado"
            )
        }

        if (!authToken) {
            throw new Error(
                "Token de autenticação é obrigatório para rastreamento em lote"
            )
        }

        const body: BatchTrackingRequest = {
            rastreamentos: trackingItems.map((item) => ({
                notafiscal: {
                    numero: item.numero,
                    serie: item.serie
                }
            }))
        }

        try {
            const response = await axios.post(BATCH_ALL_OCCURRENCES_TRK, body, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authToken
                },
                timeout: 30000
            })

            if (response.data && Array.isArray(response.data)) {
                return response.data.map((item: any) => ({
                    notafiscal: {
                        cnpjFornecedor:
                            item.cnpjFornecedor ||
                            item.notafiscal?.cnpjFornecedor,
                        pedido: item.pedido || item.notafiscal?.pedido,
                        numero: item.numero || item.notafiscal?.numero || "",
                        serie: item.serie || item.notafiscal?.serie || "",
                        cpfCnpjDestinatario:
                            item.cpfCnpjDestinatario ||
                            item.notafiscal?.cpfCnpjDestinatario,
                        destinatario:
                            item.destinatario ||
                            item.notafiscal?.destinatario ||
                            "",
                        cnpjTransportadora:
                            item.cnpjTransportadora ||
                            item.notafiscal?.cnpjTransportadora ||
                            "",
                        emissao: item.emissao || item.notafiscal?.emissao,
                        dataExpedicao:
                            item.dataExpedicao ||
                            item.notafiscal?.dataExpedicao,
                        remetente: item.remetente || item.notafiscal?.remetente,
                        datalimite:
                            item.datalimite || item.notafiscal?.datalimite,
                        ufRementente:
                            item.ufRementente || item.notafiscal?.ufRementente,
                        endRem: item.endRem || item.notafiscal?.endRem,
                        munRem: item.munRem || item.notafiscal?.munRem,
                        bairroRem: item.bairroRem || item.notafiscal?.bairroRem,
                        cepRem: item.cepRem || item.notafiscal?.cepRem,
                        Status: item.status || item.Status || "pendente",
                        endereco: {
                            logradouro: item.endereco?.logradouro,
                            bairro: item.endereco?.bairro,
                            municipio:
                                item.endereco?.municipio ||
                                item.municipio ||
                                "",
                            uf: item.endereco?.uf || item.uf || "",
                            cep: item.endereco?.cep
                        },
                        linkComprovante: item.linkComprovante || ""
                    },
                    ocorrencias: (item.ocorrencias || []).map(
                        (ocorrencia: any) => ({
                            numOcorrencia: ocorrencia.numOcorrencia,
                            codigo: ocorrencia.codigo || "",
                            descricao: ocorrencia.descricao || "",
                            codigoKronos: ocorrencia.codigoKronos,
                            descricaoKronos: ocorrencia.descricaoKronos,
                            status: ocorrencia.status || "",
                            momento: ocorrencia.momento || ""
                        })
                    )
                }))
            } else {
                throw new Error("Formato de resposta inválido do servidor")
            }
        } catch (error: any) {
            if (error.response) {
                const errorMessage =
                    error.response.data?.message ||
                    error.response.data?.mensagem ||
                    `Erro da API: ${error.response.status}`
                throw new Error(errorMessage)
            } else if (error.request) {
                if (error.code === "ECONNABORTED") {
                    throw new Error("Tempo limite de conexão excedido")
                }
                throw new Error("Erro de conexão com o servidor")
            } else {
                throw new Error(error.message || "Erro desconhecido")
            }
        }
    },

    getBatchAllOccurrencesUnit: async (
        trackingItems: Array<{ numero: string; serie: string }>,
        authToken: string
    ): Promise<BatchTrackingResponse[]> => {
        if (!REACT_APP_BASE_URL_BATCH_ALL_OCCURRENCES_UNIT) {
            throw new Error(
                "Endpoint de rastreamento em lote com todas as ocorrências não configurado"
            )
        }

        if (!authToken) {
            throw new Error(
                "Token de autenticação é obrigatório para rastreamento em lote"
            )
        }

        const body: BatchTrackingRequest = {
            rastreamentos: trackingItems.map((item) => ({
                notafiscal: {
                    numero: item.numero,
                    serie: item.serie
                }
            }))
        }

        try {
            const response = await axios.post(
                REACT_APP_BASE_URL_BATCH_ALL_OCCURRENCES_UNIT,
                body,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authToken
                    },
                    timeout: 30000
                }
            )

            if (response.data && Array.isArray(response.data)) {
                return response.data.map((item: any) => ({
                    notafiscal: {
                        cnpjFornecedor:
                            item.cnpjFornecedor ||
                            item.notafiscal?.cnpjFornecedor,
                        pedido: item.pedido || item.notafiscal?.pedido,
                        numero: item.numero || item.notafiscal?.numero || "",
                        serie: item.serie || item.notafiscal?.serie || "",
                        cpfCnpjDestinatario:
                            item.cpfCnpjDestinatario ||
                            item.notafiscal?.cpfCnpjDestinatario,
                        destinatario:
                            item.destinatario ||
                            item.notafiscal?.destinatario ||
                            "",
                        cnpjTransportadora:
                            item.cnpjTransportadora ||
                            item.notafiscal?.cnpjTransportadora ||
                            "",
                        emissao: item.emissao || item.notafiscal?.emissao,
                        dataExpedicao:
                            item.dataExpedicao ||
                            item.notafiscal?.dataExpedicao,
                        remetente: item.remetente || item.notafiscal?.remetente,
                        datalimite:
                            item.datalimite || item.notafiscal?.datalimite,
                        ufRementente:
                            item.ufRementente || item.notafiscal?.ufRementente,
                        endRem: item.endRem || item.notafiscal?.endRem,
                        munRem: item.munRem || item.notafiscal?.munRem,
                        bairroRem: item.bairroRem || item.notafiscal?.bairroRem,
                        cepRem: item.cepRem || item.notafiscal?.cepRem,
                        Status: item.status || item.Status || "pendente",
                        endereco: {
                            logradouro: item.endereco?.logradouro,
                            bairro: item.endereco?.bairro,
                            municipio:
                                item.endereco?.municipio ||
                                item.municipio ||
                                "",
                            uf: item.endereco?.uf || item.uf || "",
                            cep: item.endereco?.cep
                        },
                        linkComprovante: item.linkComprovante || ""
                    },
                    ocorrencias: (item.ocorrencias || []).map(
                        (ocorrencia: any) => ({
                            numOcorrencia: ocorrencia.numOcorrencia,
                            codigo: ocorrencia.codigo || "",
                            descricao: ocorrencia.descricao || "",
                            codigoKronos: ocorrencia.codigoKronos,
                            descricaoKronos: ocorrencia.descricaoKronos,
                            status: ocorrencia.status || "",
                            momento: ocorrencia.momento || ""
                        })
                    )
                }))
            } else {
                throw new Error("Formato de resposta inválido do servidor")
            }
        } catch (error: any) {
            if (error.response) {
                const errorMessage =
                    error.response.data?.message ||
                    error.response.data?.mensagem ||
                    `Erro da API: ${error.response.status}`
                throw new Error(errorMessage)
            } else if (error.request) {
                if (error.code === "ECONNABORTED") {
                    throw new Error("Tempo limite de conexão excedido")
                }
                throw new Error("Erro de conexão com o servidor")
            } else {
                throw new Error(error.message || "Erro desconhecido")
            }
        }
    },

    // Método para verificar status do endpoint de lote
    checkBatchEndpointStatus: async (authToken: string): Promise<boolean> => {
        if (!BATCH_TRK) {
            return false
        }

        try {
            const response = await axios.get(BATCH_TRK, {
                headers: {
                    Authorization: authToken
                },
                timeout: 10000
            })
            return response.status === 200
        } catch (error) {
            return false
        }
    }
}

export default TrackingService
